import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SingleSelectComponent } from '@examdojo/core/select';
import { NumberInputComponent, TextareaInputComponent, TextInputComponent } from '@examdojo/ui/input';
import { connectState } from '@examdojo/angular/util';
import { debounceTime, map, startWith } from 'rxjs';
import {
  DOCUMENT_TYPE_LIST,
  DOCUMENT_TYPE_TO_LABEL,
  SOURCE_TYPE_LIST,
  SOURCE_TYPE_TO_LABEL,
  SourceStoreModel,
} from '../source.model';
import { SourceEditorFormService } from './source-editor-form.service';

@Component({
  selector: 'dojo-source-editor',
  template: `
    <div class="p-4">
      <form [formGroup]="form" class="space-y-4">
        <dojo-text-input
          [size]="'s'"
          [formCtrl]="form.controls.name"
          [label]="'Name'"
          [errorMessages]="{ required: 'Name is required' }"
        ></dojo-text-input>

        <dojo-single-select
          class="mb-4"
          [size]="'s'"
          [label]="'Source Type'"
          [formCtrl]="form.controls.source_type"
          [optionValue]="'value'"
          [optionLabel]="'label'"
          [options]="sourceTypeOptions"
        />

        <dojo-single-select
          class="mb-4"
          [size]="'s'"
          [label]="'Document Type'"
          [formCtrl]="form.controls.document_type"
          [optionValue]="'value'"
          [optionLabel]="'label'"
          [options]="documentTypeOptions"
        />
        <dojo-textarea-input
          [size]="'s'"
          [formCtrl]="form.controls.source_ref"
          [autoResize]="true"
          [autoResizeMinRows]="4"
          [autoResizeMaxRows]="10"
          [label]="'Source Reference'"
          [errorMessages]="{ required: 'Source reference is required' }"
        ></dojo-textarea-input>
        <dojo-textarea-input
          [size]="'s'"
          [formCtrl]="form.controls.raw_source"
          [autoResize]="true"
          [autoResizeMinRows]="4"
          [autoResizeMaxRows]="10"
          [label]="'Raw Source'"
          [errorMessages]="{ required: 'Raw Source is required' }"
        ></dojo-textarea-input>
        <dojo-textarea-input
          [size]="'s'"
          [formCtrl]="form.controls.processed_content"
          [autoResize]="true"
          [autoResizeMinRows]="4"
          [autoResizeMaxRows]="10"
          [label]="state.processedContentLabel"
        ></dojo-textarea-input>
        <dojo-textarea-input
          [size]="'s'"
          [formCtrl]="form.controls.parsing_instructions"
          [autoResize]="true"
          [autoResizeMinRows]="4"
          [autoResizeMaxRows]="10"
          [label]="'Parsing Instructions'"
        ></dojo-textarea-input>
        <dojo-number-input
          [size]="'s'"
          [formCtrl]="form.controls.tier"
          [label]="'Tier'"
          [errorMessages]="{ required: 'Tier is required' }"
        ></dojo-number-input>
        <dojo-textarea-input
          [size]="'s'"
          [formCtrl]="form.controls.metadata"
          [autoResize]="true"
          [autoResizeMinRows]="4"
          [autoResizeMaxRows]="10"
          [label]="'Metadata'"
        ></dojo-textarea-input>
      </form>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    SingleSelectComponent,
    MatTooltipModule,
    TextInputComponent,
    TextareaInputComponent,
    NumberInputComponent,
  ],
})
export class SourceEditorComponent {
  constructor(private readonly sourceEditorFormService: SourceEditorFormService) {}

  readonly source = input.required<SourceStoreModel>();

  readonly form = this.sourceEditorFormService.sourceForm;

  readonly sourceTypeOptions = SOURCE_TYPE_LIST.map((value) => ({
    label: SOURCE_TYPE_TO_LABEL[value],
    value,
  }));

  readonly documentTypeOptions = DOCUMENT_TYPE_LIST.map((value) => ({
    label: DOCUMENT_TYPE_TO_LABEL[value],
    value,
  }));

  private readonly processedContentParsedItems$ = this.form.controls.processed_content.valueChanges.pipe(
    debounceTime(350),
    startWith(this.form.controls.processed_content.value),
    map((value) => {
      try {
        const parsedItems = JSON.parse(value);
        return [parsedItems['questions']?.length ?? 0, parsedItems['markschemes']?.length ?? 0];
      } catch (error) {
        return [0, 0];
      }
    }),
  );

  readonly state = connectState({
    processedContentLabel: this.processedContentParsedItems$.pipe(
      map(([questionsCount, markschemesCount]) => {
        return `Processed Content (q: ${questionsCount}, m: ${markschemesCount})`;
      }),
    ),
  });
}
